import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { dialogs, saving, i18next } from '@yola/ws-sdk';
import { Modal, PanelGroup, Panel, ProgressDialog, designSystem, reactHookForm } from '@yola/ws-ui';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';

const {
  InputGroupField,
  FieldStatus,
  Box,
  ControlGroup,
  DialogHeader,
  DialogHeaderTitle,
  DialogButton,
  DialogButtonGroup,
  DialogButtonGroupSeparator,
} = designSystem;

const { useForm } = reactHookForm;

function CreateManualSiteBackup({
  onBackupCreateSuccess = null,
  onCancel = null,
  onDialogMainAction,
  onDialogCancel,
}) {
  const dispatch = useDispatch();
  const [isPending, setIsPending] = useState(false);

  const {
    control,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      backupName: i18next.t('My site backup'),
    },
    mode: 'onTouched',
  });

  const getPromptCaptions = (values) => {
    const getDescription = (backupName) =>
      backupName.trim()
        ? i18next.t('Do you want to create the site backup with the "{backupName}" name?', {
            backupName,
          })
        : i18next.t('Do you want to create the site backup?');

    return {
      title: i18next.t('Create site backup'),
      description: getDescription(values.backupName),
      submit: i18next.t('Create'),
      discard: i18next.t('Discard'),
      cancel: i18next.t('Cancel'),
    };
  };

  const handleSuccess = () => {
    if (onBackupCreateSuccess) {
      onBackupCreateSuccess();
    } else {
      dispatch(dialogs.actions.hide());
    }
  };

  const handleCancel = () => {
    onDialogCancel();
    onCancel();
  };

  const handleError = () => {
    dialogs.operations.show(dialogTypes.SITE_BACKUP_ERROR, {
      captions: {
        title: i18next.t('Site backup creation failed'),
        description: i18next.t('An error occurred while creating your site backup'),
      },
    });
  };

  const createBackup = (values) => {
    onDialogMainAction();
    setIsPending(true);

    const options = { name: values.backupName };

    saveSiteWithEffect(() =>
      dispatch(saving.thunks.createSiteBackup(options)).then(handleSuccess).catch(handleError)
    );
  };

  const pendingProps = {
    className: 'ws-site-backup-pending-dialog',
    overlay: 'visible',
    cancelable: false,
    progressType: 'indeterminate',
    width: 290,
    height: 108,
    captions: {
      title: `${i18next.t('Creating site backup...')}`,
    },
  };

  const onFormSubmit = handleSubmit(createBackup);

  return (
    <React.Fragment>
      {isPending && <ProgressDialog {...pendingProps} />}

      {!isPending && (
        <Modal
          overlay="visible"
          centered
          resizable={false}
          draggable={false}
          className="ws-text-input-dialog"
          width={360}
          height={265}
          minWidth={312}
          minHeight={265}
          showPromptForce
          promptCaptions={getPromptCaptions(getValues())}
          handleCancel={handleCancel}
          handleSubmit={onFormSubmit}
        >
          <PanelGroup height="100%">
            <Panel theme="gray-100" height="48" align="middle" corners="squared">
              <DialogHeader>
                <DialogHeaderTitle>{i18next.t('Create site backup')}</DialogHeaderTitle>
              </DialogHeader>
            </Panel>

            <Panel className="ws-without-border-bottom">
              <Box
                paddingTop="spacing-l"
                paddingLeft="spacing-m"
                paddingRight="spacing-m"
                paddingBottom="spacing-xl"
              >
                <ControlGroup title={i18next.t('Backup name')}>
                  <InputGroupField
                    name="backupName"
                    control={control}
                    rules={{
                      required: i18next.t('Site backup name can not be empty'),
                    }}
                  />

                  {!errors.backupName && (
                    <FieldStatus
                      appearance="default"
                      text={i18next.t('Give your site backup a name')}
                    />
                  )}
                </ControlGroup>
              </Box>
            </Panel>

            <Panel>
              <DialogButtonGroup rounded>
                <DialogButton onClick={onFormSubmit}>{i18next.t('Submit')}</DialogButton>

                <DialogButtonGroupSeparator />

                <DialogButton onClick={handleCancel}>{i18next.t('Cancel')}</DialogButton>
              </DialogButtonGroup>
            </Panel>
          </PanelGroup>
        </Modal>
      )}
    </React.Fragment>
  );
}

CreateManualSiteBackup.propTypes = {
  onBackupCreateSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default CreateManualSiteBackup;
